/**
 * Plans — /plans redesign Stage 08.4 + P0 hotfix 2026-05-22.
 *
 * P0 HOTFIX (2026-05-22) per plans-design-review-2026-05-22.md:
 *   - Header safe-area pattern (HomeStatusBar.jsx clone) — убран double-padding
 *   - TrustLogosBlock перенесён вниз скролла (всегда видим, не только под card)
 *   - CTA: убран leftIcon=coin; rightIcon условный (star только для Stars)
 *   - CTA Stars text: убран inline ⭐ (теперь rightIcon)
 *
 * WAVE-4 REDESIGN (REOPENED 2026-05-22):
 *   - Native-style statusbar header + centered Raleway Bold 18 «Подписка»
 *   - PaymentMethodPicker square cards 86.5×98 (Figma 319:4631/4831/4931/40000032:6614)
 *   - «Метод оплаты» label above picker (Raleway Medium 12, text-text-secondary)
 *   - Multi-currency: prices/CTA change by selected method
 *   - CTA «Купить за …» fixed bottom container 402×88
 *
 * Figma sources:
 *   Карта:  319:4631 — $49.50 / $14.50 / Free
 *   Крипта: 319:4831 — $49.50 / $14.50 / Free
 *   СБП:    319:4931 — 4 950 ₽ / 1 450 ₽ / Free
 *   Звёзды: 40000032:6614 — 2 475 ⭐ / 725 ⭐ / Free
 *
 * Token compliance: no bg-[#...], no hex literals in Tailwind classes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useTelegram } from '../hooks/useTelegram';
import PaymentMethodPicker from '../components/payments/PaymentMethodPicker';
import CryptoCurrencyPicker, {
  DEFAULT_CRYPTO_CURRENCY,
} from '../components/payments/CryptoCurrencyPicker';
import CryptoPaymentDetails from '../components/payments/CryptoPaymentDetails';
import TrustLogosBlock from '../components/payments/TrustLogosBlock';
import PlanCard from '../components/plans/PlanCard';
import DowngradePopup from '../components/plans/DowngradePopup';
import { UIButton } from '../components/primitives';
import { getDefaultMethod } from '../utils/payment-availability';
import { resolveActivePlanKey } from '../core/accountPresentation';
import {
  useProfileQuery,
  useSubscriptionCurrentQuery,
  useSubscriptionPlansQuery,
} from '../core/queries/account';
import {
  useSubscriptionCheckoutP4yMutation,
  useSubscriptionCheckoutStripeMutation,
  useSubscriptionInvoiceTelegramMutation,
} from '../core/queries/mutations';
import { useSubscriptionPaymentStatusQuery } from '../core/queries/paymentStatus';
import { queryKeys } from '../core/queries/queryKeys';
import { getLanguageCode, openExternalUrl, purchase } from '../platform';
import { trackClientEvent } from '../analytics/track';
import useConsentGuard from '../hooks/useConsentGuard';

// Static features list per plan (from acceptance §14, hardcoded as Figma shows them)
const PLAN_FEATURES = {
  ULTRA: 'ultra',
  PREMIUM: 'premium',
  FREE: 'free',
};

const STARS_PRICE = { ULTRA: 2475, PREMIUM: 725, FREE: 0 };

const SBP_PRICE = { ULTRA: 4950, PREMIUM: 1450, FREE: 0 };

// Plan rank for downgrade detection
const PLAN_RANK = { FREE: 0, PREMIUM: 1, ULTRA: 2 };

// Marketing fallback — backend /subscription/plans не возвращает marketing,
// а Figma 319:4656/4657 требует discount chip и «Лучший выбор» на Ultra.
const MARKETING_FALLBACK = {
  ULTRA:   { isBestChoice: true,  discountPercent: 50, oldPrice: 99 },
  PREMIUM: { isBestChoice: false, discountPercent: 50, oldPrice: 29 },
  FREE:    null,
};

function isDowngrade(targetPlan, currentPlan) {
  const target = (targetPlan || 'FREE').toUpperCase();
  const current = (currentPlan || 'FREE').toUpperCase();
  return (PLAN_RANK[target] ?? 0) < (PLAN_RANK[current] ?? 0);
}

/** Narrow no-break space thousands separator (U+202F per Figma spec) */
function formatThousands(n) {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * Format price for CTA text depending on method.
 * Stars: число без `⭐` — звезда теперь как rightIcon на UIButton (Figma 40000032:6643).
 */
function formatCtaPrice(plan, method, freeText) {
  const planKey = (plan?.id || 'FREE').toUpperCase();
  if (planKey === 'FREE') return freeText;
  if (method === 'stars') return formatThousands(STARS_PRICE[planKey] ?? 0);
  if (method === 'sbp') return `${formatThousands(SBP_PRICE[planKey] ?? 0)} ₽`;
  return `$${plan?.price ?? 0}`;
}

function formatPlanCredits(plan, t) {
  const planKey = (plan?.id || 'FREE').toUpperCase();
  if (planKey === 'FREE') {
    return t('plans.free.credits', { count: plan?.signup_credits ?? 10 });
  }
  return `${formatThousands(plan?.monthly_credits ?? 0)} ${t('common.credits')} ${t('common.month')}`;
}

export default function Plans() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuthStore();
  const { hapticFeedback } = useTelegram();
  const { requireConsent } = useConsentGuard();

  // ─── PaymentMethodPicker state (local — NOT shared with Credits) ───
  const [method, setMethod] = useState(getDefaultMethod());
  const [cryptoCurrency, setCryptoCurrency] = useState(DEFAULT_CRYPTO_CURRENCY);
  const [showCryptoPicker, setShowCryptoPicker] = useState(false);
  const [cryptoPayment, setCryptoPayment] = useState(null);

  // ─── Telegram language ───
  const telegramLang = getLanguageCode() || null;
  const paymentLocale = String(
    i18n?.resolvedLanguage || i18n?.language || telegramLang || '',
  ).toLowerCase().startsWith('ru') ? 'ru' : '';

  const plansQuery = useSubscriptionPlansQuery();
  const subscriptionQuery = useSubscriptionCurrentQuery();
  const profileQuery = useProfileQuery();
  const starsCheckoutMutation = useSubscriptionInvoiceTelegramMutation();
  const p4yCheckoutMutation = useSubscriptionCheckoutP4yMutation();
  const stripeCheckoutMutation = useSubscriptionCheckoutStripeMutation();
  const paymentStatusQuery = useSubscriptionPaymentStatusQuery(cryptoPayment?.payment_id, {
    enabled: Boolean(cryptoPayment?.payment_id),
    expiresAt: cryptoPayment?.expires_at,
  });
  const cryptoPaymentStatus = paymentStatusQuery.data?.status;
  const cryptoPaymentFailed =
    paymentStatusQuery.isPollingTimedOut ||
    cryptoPaymentStatus === 'failed' ||
    cryptoPaymentStatus === 'expired' ||
    paymentStatusQuery.isError;

  // ─── Checkout loading per plan ───
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);

  // ─── Downgrade popup ───
  const [downgradeTarget, setDowngradeTarget] = useState(null);

  // ─── Selected plan for CTA (null = no plan selected yet, shows first non-current) ───
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    if (location.state?.paywallAlreadyTracked) return;
    trackClientEvent('paywall_shown', {
      trigger: location.state?.paywallTrigger || 'manual',
    });
  }, [location.key, location.state]);

  useEffect(() => {
    if (method !== 'crypto') {
      setShowCryptoPicker(false);
    }
    if (method !== 'crypto' && method !== 'sbp') {
      setCryptoPayment(null);
    }
  }, [method]);

  useEffect(() => {
    if (paymentStatusQuery.data?.status !== 'paid') return;

    refreshUser?.();
    queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionCurrent() });
    queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPlans() });
  }, [paymentStatusQuery.data?.status, queryClient, refreshUser]);

  // ─── Checkout ───
  const handleCheckout = useCallback(
    async (planId) => {
      hapticFeedback?.('medium');
      setCheckoutLoading(planId);
      setCheckoutError(null);

      const normalizedPlan = planId.toLowerCase();

      try {
        if (method === 'stars') {
          const res = await starsCheckoutMutation.mutateAsync({
            plan: normalizedPlan,
          });
          const invoiceUrl = res?.invoice_url;
          if (invoiceUrl) {
            purchase(
              {
                productId: `subscription:${normalizedPlan}`,
                checkoutUrl: invoiceUrl,
              },
              {
                onStatus: async (status) => {
                  if (status === 'paid') {
                    hapticFeedback?.('success');
                    await refreshUser?.();
                    queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
                    queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionCurrent() });
                    queryClient.invalidateQueries({ queryKey: queryKeys.subscriptionPlans() });
                    navigate('/');
                  }
                },
              },
            );
          } else {
            setCheckoutError(t('plans.starsInvoiceError'));
          }
        } else if (method === 'crypto' || method === 'sbp') {
          const res = await p4yCheckoutMutation.mutateAsync({
            plan: normalizedPlan,
            paymentMethod: method,
            cryptoCurrency: method === 'crypto' ? cryptoCurrency : 'sbp',
          });
          if (res?.address) {
            setCryptoPayment(res);
          } else {
            setCheckoutError(t('plans.checkoutSessionError'));
          }
        } else {
          const res = await stripeCheckoutMutation.mutateAsync({
            plan: normalizedPlan,
          });
          const checkoutUrl = res?.checkout_url;
          if (checkoutUrl) {
            if (!openExternalUrl(checkoutUrl)) window.location.assign(checkoutUrl);
          } else {
            setCheckoutError(t('plans.checkoutSessionError'));
          }
        }
      } catch (err) {
        hapticFeedback?.('error');
        const msg =
          err.response?.data?.error ||
          err.message ||
          t('plans.checkoutError');
        setCheckoutError(msg);
      } finally {
        setCheckoutLoading(null);
      }
    },
    [
      method,
      cryptoCurrency,
      navigate,
      queryClient,
      hapticFeedback,
      refreshUser,
      t,
      starsCheckoutMutation,
      p4yCheckoutMutation,
      stripeCheckoutMutation,
    ],
  );

  // ─── Card tap — pure selection, no side effects (Figma 319:4631 radio model) ───
  const handleSelectPlan = useCallback(
    (planId) => {
      hapticFeedback?.('light');
      setSelectedPlanId(planId);
    },
    [hapticFeedback],
  );

  // ─── Render plans in Figma order: Ultra → Premium → Free ───
  const plans = plansQuery.data?.plans ?? [];
  const ORDERED_IDS = ['ULTRA', 'PREMIUM', 'FREE'];
  const orderedPlans = ORDERED_IDS.map((id) =>
    plans.find((p) => p.id === id),
  ).filter(Boolean);
  const subscription = subscriptionQuery.data?.subscription ?? null;
  const subscriptionInfo = subscription
    ? {
        expiresAt: subscription.expiresAt,
        daysLeft: subscription.expiresAt
          ? Math.max(0, Math.ceil((new Date(subscription.expiresAt) - Date.now()) / 86_400_000))
          : null,
      }
    : null;

  // CTA plan = selectedPlanId or first non-current plan
  // Prefer authStore user; fall back to /user/profile fetched locally.
  const effectiveUser = profileQuery.data?.user ?? profileQuery.data ?? user ?? null;
  const currentPlanKey = resolveActivePlanKey({ subscription, user: effectiveUser });
  const ctaPlan =
    orderedPlans.find((p) => (selectedPlanId ? p.id === selectedPlanId : p.id !== currentPlanKey)) ??
    orderedPlans[0];
  const isCryptoChoiceStep = method === 'crypto' && showCryptoPicker;
  const isCryptoPaymentStep = Boolean(cryptoPayment);
  const loading = plansQuery.isLoading || subscriptionQuery.isLoading || profileQuery.isLoading;
  const apiError = plansQuery.isError || subscriptionQuery.isError || profileQuery.isError
    ? t('plans.loadError')
    : null;

  // ─── CTA «Купить за …» — runs downgrade-detection THEN checkout ───
  const performCtaConfirm = useCallback(() => {
    if (!ctaPlan) return;
    hapticFeedback?.('medium');
    const currentPlan = currentPlanKey;
    if (isDowngrade(ctaPlan.id, currentPlan)) {
      setDowngradeTarget(ctaPlan.id);
      return;
    }
    if (method === 'crypto' && !showCryptoPicker) {
      setShowCryptoPicker(true);
      setCheckoutError(null);
      return;
    }
    handleCheckout(ctaPlan.id);
  }, [ctaPlan, currentPlanKey, hapticFeedback, method, showCryptoPicker, handleCheckout]);

  const handleCtaConfirm = useCallback(() => {
    requireConsent(performCtaConfirm, 'purchase');
  }, [performCtaConfirm, requireConsent]);

  // ─── Downgrade confirm ───
  const handleDowngradeConfirm = useCallback(async () => {
    if (!downgradeTarget) return;
    await handleCheckout(downgradeTarget);
    setDowngradeTarget(null);
  }, [downgradeTarget, handleCheckout]);

  const handleDowngradeCancel = useCallback(() => {
    setDowngradeTarget(null);
  }, []);

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-bg-app flex flex-col relative">
      {/* ── Statusbar header (Figma 319:4632 — 96px gradient + shine glow + title) ──
          Safe-area pattern mirrors HomeStatusBar.jsx — НЕ hardcoded height,
          paddingTop = safe-top + 12 (воздух от системного бара до title). */}
      <div
        className="relative shrink-0"
        style={{
          paddingTop: 'calc(var(--safe-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}
      >
        {/* Scrim gradient #000 → transparent (Figma statusbar fill). */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
          }}
        />
        {/* Shine ellipse 104×104 #FFAC0B blur 170 (Figma 318:3534). */}
        <div
          aria-hidden
          data-testid="plans-statusbar-shine"
          className="absolute top-0 left-[31px] w-[104px] h-[104px] rounded-full pointer-events-none"
          style={{ background: '#FFAC0B', filter: 'blur(170px)' }}
        />
        <h1
          className="relative font-sans font-bold text-[18px] leading-[21.13px] tracking-[0.36px] text-text-primary text-center"
          style={{
            transform: 'translateY(var(--page-title-shift, 47px))',
            maxWidth: 'calc(100% - 200px)',
            marginInline: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {t('subscription.title')}
        </h1>
      </div>

      {/* ── Scrollable content ──
          pb рассчитывается под fixed CTA-zone (88) + safe-bottom + воздух. */}
      <div
        className="flex-1 overflow-y-auto"
        style={{
          paddingTop: 'var(--page-title-clearance, 48px)',
          paddingBottom: 'calc(88px + env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {isCryptoPaymentStep && (
          <>
            <CryptoPaymentDetails
              payment={cryptoPayment}
              onBack={() => setCryptoPayment(null)}
            />
            {cryptoPaymentStatus !== 'paid' && !cryptoPaymentFailed && (
              <p className="px-4 pb-4 text-center font-sans text-[12px] text-text-secondary">
                {t('success.waitingConfirmation', 'Waiting for payment confirmation...')}
              </p>
            )}
            {cryptoPaymentFailed && (
              <p className="px-4 pb-4 text-center font-sans text-[12px] text-text-secondary" role="alert">
                {t('success.confirmationTimeout', 'Payment is not confirmed yet. If you paid, reopen the app later and the balance will refresh automatically.')}
              </p>
            )}
          </>
        )}

        {!isCryptoPaymentStep && !isCryptoChoiceStep && (
          <>
            {/* «Метод оплаты» label */}
            <div className="px-4 mb-2">
              <span className="font-sans font-medium text-[12px] leading-[12px] text-text-secondary">
                {t('plans.paymentMethod')}
              </span>
            </div>

            {/* ── PaymentMethodPicker (square cards) ── */}
            <div className="px-4 mb-4">
              <PaymentMethodPicker
                value={method}
                onChange={setMethod}
                locale={paymentLocale}
                telegramLang={telegramLang}
              />
            </div>
          </>
        )}

        {!isCryptoPaymentStep && isCryptoChoiceStep && ctaPlan && (
          <div className="px-4 mb-4 space-y-3">
            <section className="rounded-home-16 bg-glass-8 border border-border-default p-4">
              <p className="font-sans font-medium text-[11px] leading-[13px] text-text-secondary">
                {t('plans.cryptoPicker.selected')}
              </p>
              <div className="mt-2 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-sans font-bold text-[20px] leading-[23px] text-text-primary">
                    {ctaPlan.name}
                  </p>
                  <p className="mt-1 font-sans font-semibold text-[12px] leading-[14px] text-text-secondary">
                    {formatPlanCredits(ctaPlan, t)}
                  </p>
                </div>
                <p className="shrink-0 font-sans font-extrabold text-[24px] leading-[28px] text-text-primary">
                  {formatCtaPrice(ctaPlan, method, t('plans.freePrice'))}
                </p>
              </div>
            </section>
            <CryptoCurrencyPicker
              value={cryptoCurrency}
              onChange={setCryptoCurrency}
            />
          </div>
        )}

        {/* ── Loading skeleton ── */}
        {loading && (
          <div className="px-4 flex flex-col gap-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-home-24 h-48 skeleton-shimmer"
                style={{ '--skeleton-base': '#151517' }}
                aria-hidden="true"
              />
            ))}
          </div>
        )}

        {/* ── Error state ── */}
        {!loading && apiError && (
          <div className="px-4">
            <p className="font-sans text-[14px] text-text-secondary text-center py-8">
              {apiError}
            </p>
          </div>
        )}

        {/* ── Plan cards (Figma 319:4655 itemSpacing=8) ── */}
        {!loading && !apiError && !isCryptoPaymentStep && !isCryptoChoiceStep && (
          <div className="px-4 flex flex-col gap-2">
            {orderedPlans.map((plan) => {
              const planKey = plan.id.toUpperCase();
              const isCurrent = currentPlanKey === planKey;
              const featureKey = PLAN_FEATURES[planKey];
              const features = featureKey
                ? t(`plans.${featureKey}.features`, {
                    returnObjects: true,
                    count: plan.signup_credits ?? 10,
                  })
                : [];

              return (
                <PlanCard
                  key={plan.id}
                  id={plan.id}
                  name={plan.name}
                  price={plan.price}
                  monthlyCredits={plan.monthly_credits}
                  signupCredits={plan.signup_credits}
                  features={features}
                  method={method}
                  starsPrice={STARS_PRICE[planKey] ?? 0}
                  sbpPrice={SBP_PRICE[planKey] !== 0 ? SBP_PRICE[planKey] : null}
                  marketing={plan.marketing ?? MARKETING_FALLBACK[planKey] ?? null}
                  isCurrent={isCurrent}
                  isSelected={ctaPlan?.id === plan.id && !isCurrent}
                  onSelect={() => handleSelectPlan(plan.id)}
                />
              );
            })}
          </div>
        )}

        {/* ── TrustLogosBlock — Figma 319:4659 always visible at bottom of scroll
            (между списком планов и CTA-zone). Перенесён сверху сюда per P0-2. */}
        {!loading && !apiError && !isCryptoPaymentStep && !isCryptoChoiceStep && (
          <div className="px-4 mt-4">
            <TrustLogosBlock />
          </div>
        )}

        {/* ── Checkout inline error ── */}
        {checkoutError && !isCryptoPaymentStep && (
          <div className="px-4 mt-4">
            <p className="font-sans text-[14px] text-text-secondary text-center">
              {checkoutError}
            </p>
          </div>
        )}
      </div>

      {/* ── Fixed CTA container (Figma 40000032:6642 — 402×88, top border
          #fff@16, bg #fff@4, BACKGROUND_BLUR radius=32) ──
          P0-3: leftIcon удалён, rightIcon=star только для Stars (Figma 40000032:6643). */}
      {!loading && !apiError && !isCryptoPaymentStep && ctaPlan && ctaPlan.id !== currentPlanKey && (
        <div
          className="fixed bottom-0 left-0 right-0 z-10 flex items-center justify-center"
          style={{
            height: 88,
            borderTop: '1px solid rgba(255,255,255,0.16)',
            backgroundColor: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(32px)',
            WebkitBackdropFilter: 'blur(32px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <UIButton
            type="primary"
            size="lg"
            rightIcon={method === 'stars' ? 'star' : undefined}
            onClick={handleCtaConfirm}
            disabled={!!checkoutLoading}
            className="bg-primary-btn-gradient"
            style={{
              width: 370,
              opacity: checkoutLoading ? 0.7 : 1,
            }}
            data-testid="plans-cta-btn"
          >
            {checkoutLoading
              ? t('common.processing')
              : method === 'crypto' && showCryptoPicker
                ? `${t('plans.cryptoPicker.pay')} ${formatCtaPrice(ctaPlan, method, t('plans.freePrice'))}`
                : `${t('credits.buyFor')} ${formatCtaPrice(ctaPlan, method, t('plans.freePrice'))}`}
          </UIButton>
        </div>
      )}

      {/* ── Downgrade popup ── */}
      {downgradeTarget && (
        <DowngradePopup
          fromPlan={currentPlanKey}
          toPlan={downgradeTarget}
          daysLeft={subscriptionInfo?.daysLeft ?? null}
          changeDate={subscriptionInfo?.expiresAt ?? null}
          loading={checkoutLoading === downgradeTarget}
          onConfirm={handleDowngradeConfirm}
          onCancel={handleDowngradeCancel}
        />
      )}
    </div>
  );
}
